import { motion } from 'motion/react';
import { useMemo, useState, useEffect } from 'react';
import type { Position } from '@/types/game';
import Character3D, { type CharacterConfig } from './Character3D';
import type { CharacterProp } from '@/types/props';

// Deep partial type helper
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

interface CharacterProps {
  position: Position;
  direction: 'up' | 'down' | 'left' | 'right' | null;
  isMoving: boolean;
  cellSize: number;
  hasCollided?: boolean;
  gameState?: 'playing' | 'won' | 'lost' | 'entering' | 'exiting';
  onEntranceComplete?: () => void;
  onTap?: () => void;
  style?: 'flat' | '3d';
  currentTileType?: string;
  config?: DeepPartial<CharacterConfig>;
  equippedProps?: CharacterProp[];
}

const Character: React.FC<CharacterProps> = ({
  position,
  direction,
  isMoving,
  cellSize,
  hasCollided = false,
  gameState = 'playing',
  onEntranceComplete,
  onTap,
  style = 'flat',
  currentTileType,
  config,
  equippedProps
}) => {
  // If 3D style requested, use Character3D component
  if (style === '3d') {
    return (
      <Character3D
        position={position}
        direction={direction}
        isMoving={isMoving}
        cellSize={cellSize}
        hasCollided={hasCollided}
        gameState={gameState}
        onEntranceComplete={onEntranceComplete}
        onTap={onTap}
        currentTileType={currentTileType}
        config={config}
        equippedProps={equippedProps}
      />
    );
  }
  const [isBlinking, setIsBlinking] = useState(false);
  const [isIdleAnimating, setIsIdleAnimating] = useState(false);
  const [entrancePhase, setEntrancePhase] = useState<'outside' | 'jumping' | 'landed'>('outside');

  // Handle entrance animation
  useEffect(() => {
    if (gameState === 'entering') {
      setEntrancePhase('outside');
      setTimeout(() => setEntrancePhase('jumping'), 100);
      setTimeout(() => {
        setEntrancePhase('landed');
        setTimeout(() => onEntranceComplete?.(), 200);
      }, 600);
    }
  }, [gameState, onEntranceComplete]);

  // Blink animation (disabled during special states)
  useEffect(() => {
    if (gameState === 'won' || gameState === 'lost' || gameState === 'entering' || gameState === 'exiting') {
      return;
    }
    
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(blinkInterval);
  }, [gameState]);

  // Idle animation trigger
  useEffect(() => {
    let idleTimer: NodeJS.Timeout;
    
    if (!isMoving) {
      idleTimer = setTimeout(() => {
        setIsIdleAnimating(true);
      }, 4000); // Start idle after 4 seconds of no movement
    } else {
      setIsIdleAnimating(false);
    }

    return () => clearTimeout(idleTimer);
  }, [isMoving]);

  // Calculate rotation based on direction
  const rotation = useMemo(() => {
    switch (direction) {
      case 'up':
        return { x: -20, y: 0 };
      case 'down':
        return { x: 20, y: 0 };
      case 'left':
        return { x: 0, y: -20 };
      case 'right':
        return { x: 0, y: 20 };
      default:
        return { x: 0, y: 0 };
    }
  }, [direction]);

  // Calculate stretch/squash based on movement direction
  const stretchScale = useMemo(() => {
    if (hasCollided) {
      // Bounce effect on collision
      return { scaleX: 1.3, scaleY: 0.7 };
    }
    if (!isMoving || !direction) {
      return { scaleX: 1, scaleY: 1 };
    }
    
    // Stretch in direction of movement
    switch (direction) {
      case 'up':
      case 'down':
        return { scaleX: 0.9, scaleY: 1.1 };
      case 'left':
      case 'right':
        return { scaleX: 1.1, scaleY: 0.9 };
      default:
        return { scaleX: 1, scaleY: 1 };
    }
  }, [direction, isMoving, hasCollided]);

  // Bubble propulsion effect
  const bubbleVariants = {
    idle: {
      scale: 0,
      opacity: 0,
    },
    moving: {
      scale: [0, 1.2, 0],
      opacity: [0, 0.6, 0],
      transition: {
        duration: 0.6,
        repeat: Infinity,
        ease: 'easeOut' as const,
      },
    },
  };

  // Character bounce animation
  const characterVariants = {
    idle: {
      y: 0,
    },
    moving: {
      y: [-2, 2, -2],
      transition: {
        duration: 0.3,
        repeat: Infinity,
        ease: 'easeInOut' as const,
      },
    },
  };

  // Calculate position based on animation state
  const characterPosition = useMemo(() => {
    if (gameState === 'entering') {
      if (entrancePhase === 'outside') {
        return {
          left: position[0] * cellSize,
          top: -cellSize * 2, // Start above the board
        };
      } else if (entrancePhase === 'jumping') {
        return {
          left: position[0] * cellSize,
          top: position[1] * cellSize - cellSize * 0.5, // Jump arc
        };
      }
    }
    if (gameState === 'exiting') {
      return {
        left: position[0] * cellSize,
        top: position[1] * cellSize + cellSize * 0.3, // Slight drop before vanishing
      };
    }
    return {
      left: position[0] * cellSize,
      top: position[1] * cellSize,
    };
  }, [position, cellSize, gameState, entrancePhase]);

  return (
    <motion.div
      className="character-container"
      style={{
        position: 'absolute',
        width: cellSize,
        height: cellSize,
      }}
      animate={{
        ...characterPosition,
        rotate: isIdleAnimating ? [0, -5, 5, -5, 0] : 0,
        scale: gameState === 'exiting' ? 0 : gameState === 'entering' && entrancePhase === 'jumping' ? [0.8, 1.2, 1] : 1,
        opacity: gameState === 'exiting' ? 0 : 1,
      }}
      transition={{
        left: {
          type: 'spring',
          stiffness: 300,
          damping: 25,
          duration: 0.3,
        },
        top: {
          type: 'spring',
          stiffness: 300,
          damping: 25,
          duration: 0.3,
        },
        rotate: {
          duration: 4,
          repeat: isIdleAnimating ? Infinity : 0,
          ease: 'easeInOut',
        },
        scale: {
          duration: gameState === 'entering' && entrancePhase === 'jumping' ? 0.6 : 0.3,
          ease: 'easeOut',
        },
        opacity: {
          duration: 0.3,
        },
      }}
    >
      {/* Bubble propulsion effect */}
      <motion.div
        className="bubble-effect"
        style={{
          position: 'absolute',
          width: '120%',
          height: '120%',
          left: '-10%',
          top: '-10%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(100, 200, 255, 0.3) 0%, transparent 70%)',
          filter: 'blur(2px)',
          zIndex: -1,
        }}
        variants={bubbleVariants}
        animate={isMoving ? 'moving' : 'idle'}
      />

      {/* Main character cube */}
      <motion.div
        className="character-cube"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          transformOrigin: 'center center',
        }}
        animate={{
          rotateX: rotation.x,
          rotateY: rotation.y,
          scaleX: stretchScale.scaleX,
          scaleY: stretchScale.scaleY,
        }}
        transition={{
          type: 'spring',
          stiffness: hasCollided ? 600 : 300,
          damping: hasCollided ? 10 : 20,
        }}
      >
        <motion.div
          className="cube-wrapper"
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            transformStyle: 'preserve-3d',
          }}
          variants={characterVariants}
          animate={isMoving ? 'moving' : 'idle'}
        >
          {/* Front face */}
          <div
            className="cube-face front"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
              borderRadius: '20%',
              border: '3px solid rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              transform: 'translateZ(calc(var(--cell) / 2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              zIndex: 999
            }}
          >
            {/* Face container */}
            <div style={{ 
              position: 'absolute',
              width: '80%',
              height: '60%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {/* Eyes container */}
              <div style={{ 
                position: 'relative', 
                width: '100%', 
                height: '40%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '25%',
              }}>
                {/* Left eye */}
                <motion.div
                  className="eye left"
                  style={{
                    position: 'relative',
                    width: '22%',
                    height: isBlinking ? '10%' : '70%',
                    background: '#000',
                    borderRadius: '50%',
                  }}
                  animate={{
                    y: isIdleAnimating ? [-1, 1, -1] : 0,
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  {/* Eye shine */}
                  {!isBlinking && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '20%',
                        left: '25%',
                        width: '35%',
                        height: '35%',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </motion.div>

                {/* Right eye */}
                <motion.div
                  className="eye right"
                  style={{
                    position: 'relative',
                    width: '22%',
                    height: isBlinking ? '10%' : '70%',
                    background: '#000',
                    borderRadius: '50%',
                  }}
                  animate={{
                    y: isIdleAnimating ? [-1, 1, -1] : 0,
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.1,
                  }}
                >
                  {/* Eye shine */}
                  {!isBlinking && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '20%',
                        left: '25%',
                        width: '35%',
                        height: '35%',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </motion.div>
              </div>

              {/* Mouth */}
              <motion.div
                className="mouth"
                style={{
                  position: 'absolute',
                  bottom: '15%',
                  width: '35%',
                  height: gameState === 'won' ? '25%' : '15%',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 
                    gameState === 'won' ? '10% 10% 50% 50%' : // Big smile
                    gameState === 'lost' ? '50% 50% 20% 20%' : // Frown
                    gameState === 'entering' ? '50%' : // Excited O shape for entrance
                    gameState === 'exiting' ? '10% 10% 50% 50%' : // Happy smile for exit
                    hasCollided ? '50%' : // O shape for collision
                    isMoving ? '40% 40% 60% 60%' : // Determined look while moving
                    '50% 50% 60% 60%', // Normal smile
                  transition: 'border-radius 0.3s ease',
                }}
                animate={{
                  scaleX: isIdleAnimating ? [1, 1.1, 1] : 1,
                  scaleY: hasCollided ? [1, 0.5, 1] : 1,
                }}
                transition={{
                  duration: isIdleAnimating ? 3 : 0.3,
                  repeat: isIdleAnimating ? Infinity : 0,
                  ease: 'easeInOut',
                }}
              />

              {/* Cheeks (blush when idle) */}
              {isIdleAnimating && (
                <>
                  <motion.div
                    style={{
                      position: 'absolute',
                      left: '-5%',
                      top: '50%',
                      width: '20%',
                      height: '15%',
                      background: 'radial-gradient(circle, rgba(255, 182, 193, 0.4) 0%, transparent 70%)',
                      borderRadius: '50%',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.6, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                  <motion.div
                    style={{
                      position: 'absolute',
                      right: '-5%',
                      top: '50%',
                      width: '20%',
                      height: '15%',
                      background: 'radial-gradient(circle, rgba(255, 182, 193, 0.4) 0%, transparent 70%)',
                      borderRadius: '50%',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.6, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: 0.2,
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Back face */}
          <div
            className="cube-face back"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%)',
              borderRadius: '20%',
              border: '3px solid rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              transform: 'rotateY(180deg) translateZ(calc(var(--cell) / 2))',
            }}
          />

          {/* Top face */}
          <div
            className="cube-face top"
            style={{
              position: 'absolute',
              width: '100%',
              height: '50%',
              top: '25%',
              background: 'linear-gradient(135deg, #ff8787 0%, #ff6b6b 100%)',
              borderRadius: '20%',
              border: '3px solid rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.3)',
              transform: 'rotateX(90deg) translateZ(calc(var(--cell) / 2))',
            }}
          />

          {/* Bottom face */}
          <div
            className="cube-face bottom"
            style={{
              position: 'absolute',
              width: '100%',
              height: '50%',
              top: '25%',
              background: 'linear-gradient(135deg, #cc4444 0%, #ee5a6f 100%)',
              borderRadius: '20%',
              border: '3px solid rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              transform: 'rotateX(-90deg) translateZ(calc(var(--cell) / 2))',
            }}
          />

          {/* Left face */}
          <div
            className="cube-face left"
            style={{
              position: 'absolute',
              width: '50%',
              height: '100%',
              left: '25%',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%)',
              borderRadius: '20%',
              border: '3px solid rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 2px 0 4px rgba(255, 255, 255, 0.3), inset -2px 0 4px rgba(0, 0, 0, 0.2)',
              transform: 'rotateY(-90deg) translateZ(calc(var(--cell) / 4))',
            }}
          />

          {/* Right face */}
          <div
            className="cube-face right"
            style={{
              position: 'absolute',
              width: '50%',
              height: '100%',
              left: '25%',
              background: 'linear-gradient(135deg, #ff5252 0%, #ff6b6b 100%)',
              borderRadius: '20%',
              border: '3px solid rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset -2px 0 4px rgba(255, 255, 255, 0.3), inset 2px 0 4px rgba(0, 0, 0, 0.2)',
              transform: 'rotateY(90deg) translateZ(calc(var(--cell) / 4))',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Shadow */}
      <motion.div
        className="character-shadow"
        style={{
          position: 'absolute',
          bottom: '-10%',
          left: '10%',
          width: '80%',
          height: '20%',
          background: 'radial-gradient(ellipse, rgba(0, 0, 0, 0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: -2,
        }}
        animate={{
          scale: isMoving ? [1, 1.1, 1] : 1,
          opacity: isMoving ? [0.3, 0.2, 0.3] : 0.3,
        }}
        transition={{
          duration: 0.3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </motion.div>
  );
};

export default Character;